/**
 * Sample notification inbox.
 * Replace this module when the inbox API is ready.
 * The screen already has a skeleton for that wiring.
 */

export type InboxKind =
  | 'video'
  | 'article'
  | 'mock_coins'
  | 'support_reply'
  | 'ticket_closed'
  | 'app_update'
  | 'account_sync'
  | 'data_restore'
  | 'premium'
  | 'general';

export type InboxFilter = 'all' | 'unread' | 'tutorials' | 'support' | 'updates';

export type InboxGroup = 'tutorials' | 'support' | 'updates';

export interface InboxItem {
  id: string;
  kind: InboxKind;
  title: string;
  body: string;
  time: string;
  unread: boolean;
  detail?: string;
  href?: string;
}

export interface KindMeta {
  label: string;
  hint: string;
  action: 'info' | 'link';
  group: InboxGroup;
}

export const KIND_META: Record<InboxKind, KindMeta> = {
  video: {
    label: 'Video tutorial',
    hint: 'Tap to watch video',
    action: 'info',
    group: 'tutorials',
  },
  article: {
    label: 'Article',
    hint: 'Tap to read article',
    action: 'link',
    group: 'tutorials',
  },
  mock_coins: {
    label: 'Mock Coins reward',
    hint: 'Tap to view details',
    action: 'info',
    group: 'updates',
  },
  support_reply: {
    label: 'Support reply',
    hint: 'Tap to read support reply',
    action: 'link',
    group: 'support',
  },
  ticket_closed: {
    label: 'Ticket closed',
    hint: 'Tap to view ticket',
    action: 'link',
    group: 'support',
  },
  app_update: {
    label: 'App update',
    hint: 'Tap to view details',
    action: 'info',
    group: 'updates',
  },
  account_sync: {
    label: 'Account sync',
    hint: 'Tap to view details',
    action: 'info',
    group: 'updates',
  },
  data_restore: {
    label: 'Data restore',
    hint: 'Tap to view details',
    action: 'info',
    group: 'updates',
  },
  premium: {
    label: 'Premium access',
    hint: 'Tap to view your Premium request',
    action: 'link',
    group: 'updates',
  },
  general: {
    label: 'General',
    hint: 'Tap to view details',
    action: 'info',
    group: 'updates',
  },
};

export const INBOX_FILTERS: { id: InboxFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'unread', label: 'Unread' },
  { id: 'tutorials', label: 'Tutorials' },
  { id: 'support', label: 'Support' },
  { id: 'updates', label: 'Updates' },
];

export const EMPTY_INBOX_TITLE = 'Nothing here right now';
export const EMPTY_INBOX_BODY = 'We let you know when we have something for you here.';
export const EMPTY_FILTER_TITLE = 'Nothing in this filter';
export const EMPTY_FILTER_BODY = 'Try another filter, or show every notification.';
export const VIDEO_NOTE = 'This tutorial plays in the MockLoc app.';

export const SAMPLE_INBOX: InboxItem[] = [
  {
    id: 'coins',
    kind: 'mock_coins',
    title: 'You earned Mock Coins',
    body: 'Mock Coins have been added to your account.',
    detail: '+50 Mock Coins',
    time: 'Today · 3:45 PM',
    unread: true,
  },
  {
    id: 'reply',
    kind: 'support_reply',
    title: 'Support replied to ML-2048',
    body: 'Our team answered your question about playlist playback.',
    time: 'Today · 1:10 PM',
    unread: true,
    href: '/app/queries',
  },
  {
    id: 'video',
    kind: 'video',
    title: 'Getting started with stationary mocking',
    body: 'Watch a quick walkthrough on pinning a location and starting your first mock session.',
    time: 'Yesterday · 6:20 PM',
    unread: true,
  },
  {
    id: 'article',
    kind: 'article',
    title: 'How playlist playback works',
    body: 'Read the guide on order, loop, and pause for a saved playlist.',
    time: '2 days ago',
    unread: false,
    href: '/app/articles',
  },
  {
    id: 'sync',
    kind: 'account_sync',
    title: 'Samsung F42 synced',
    body: '2 stationary points updated from the phone.',
    time: '2 days ago',
    unread: false,
  },
  {
    id: 'update',
    kind: 'app_update',
    title: 'MockLoc update available',
    body: 'Smoother route simulation and a clearer notification inbox.',
    detail: 'Version 2.5.0',
    time: '5 days ago',
    unread: false,
  },
  {
    id: 'closed',
    kind: 'ticket_closed',
    title: 'Ticket ML-1980 is closed',
    body: 'The support ticket was marked resolved.',
    time: '1 week ago',
    unread: false,
    href: '/app/queries',
  },
  {
    id: 'premium',
    kind: 'premium',
    title: 'Premium request update',
    body: 'Early community access is active until 31 Dec 2026.',
    time: '1 week ago',
    unread: false,
    href: '/app/account',
  },
  {
    id: 'restore',
    kind: 'data_restore',
    title: 'Saved data is ready',
    body: 'Open the MockLoc app on your phone to restore saved locations on that device.',
    time: '2 weeks ago',
    unread: false,
  },
  {
    id: 'general',
    kind: 'general',
    title: 'Welcome to MockLoc',
    body: 'Sign-in keeps locations, playlists, and coins on this account.',
    time: '2 weeks ago',
    unread: false,
  },
];

export function cloneInbox(items: InboxItem[]): InboxItem[] {
  return items.map((item) => ({ ...item }));
}

export function unreadCount(items: InboxItem[]): number {
  return items.reduce((sum, item) => sum + (item.unread ? 1 : 0), 0);
}

export function unreadLabel(count: number): string {
  return count === 1 ? '1 unread' : `${count} unread`;
}

export function inboxLabel(count: number): string {
  return count === 1 ? '1 notification' : `${count} notifications`;
}

export function cardLabel(item: InboxItem): string {
  const meta = KIND_META[item.kind];
  const unread = item.unread ? 'Unread. ' : '';
  return `${unread}${meta.label}. ${item.title}. ${item.time}`;
}

export function filterInbox(items: InboxItem[], filter: InboxFilter): InboxItem[] {
  if (filter === 'all') return items;
  if (filter === 'unread') return items.filter((item) => item.unread);
  return items.filter((item) => KIND_META[item.kind].group === filter);
}

export function filterCount(items: InboxItem[], filter: InboxFilter): number {
  return filterInbox(items, filter).length;
}
